// BusinessScreen — the modern (neo-fintech) trading floor. Replaces the dawn
// Play panels. Centrepiece is the Pricing Studio, which makes SUPPLY & DEMAND
// visible: drag the price and watch the demand curve — too dear and customers
// vanish, too cheap and you leave money on the table. The "sweet spot" marker is
// the price that makes the most profit. Same engine, same maths as settle.

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  businessWeeklyResult,
  findBusiness,
  findAsset,
  repFactor,
  weeklyLoanPayment,
  dayKeyFromDate,
} from '@biz-kid/engine';
import { builderContent } from '@biz-kid/engine/content/builder';
import { useDispatch, useGameState, selectHappiness } from '@biz-kid/store';
import { Card, Eyebrow, Button, Chip, Money, money, Bar } from '../../ui/kit.js';
import { CartoonBusinessScene as BusinessScene } from '../../ui/cartoon.js';

export type BusinessScreenProps = { onReflect: () => void; onClose: () => void };

export function BusinessScreen({ onReflect, onClose }: BusinessScreenProps) {
  const dispatch = useDispatch();
  const business = useGameState((s) => s.businesses[0]);
  const cash = useGameState((s) => s.wallet.cash);
  const week = useGameState((s) => s.clock.week);
  const day = useGameState((s) => s.clock.dayOfWeek);
  const dayKey = useGameState((s) => s.clock.lastWallClockDayKey);

  const advance = () => {
    const d = new Date(`${dayKey}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    dispatch({ kind: 'AdvanceDay', dayKey: dayKeyFromDate(d) });
  };

  return (
    <div className="bk-app" style={{ position: 'fixed', inset: 0, zIndex: 7, overflowY: 'auto' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'clamp(14px, 3vw, 26px)', paddingBottom: 90 }}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <Eyebrow color="var(--brand)">Drops · hype builds, sells out</Eyebrow>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>🔥 Drop Store</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip tone="brand" >{money(cash)}</Chip>
            <Button tone="ghost" size="sm" onClick={onClose}>← Home</Button>
          </div>
        </header>

        {/* Voxel atmosphere — Miko at the stand with a pixel customer. */}
        <Card pad={0} style={{ overflow: 'hidden', marginBottom: 14 }}>
          <BusinessScene />
        </Card>

        {business ? (
          <PricingStudio instanceId={business.id} defId={business.defId} price={business.price} marketing={business.marketingSpend} />
        ) : (
          <Card><p style={{ color: 'var(--text-2)' }}>No business yet.</p></Card>
        )}

        {/* Run the week */}
        <Card style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>This week</Eyebrow>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {[1, 2, 3, 4, 5].map((d) => (
                  <div
                    key={d}
                    style={{
                      width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center',
                      fontSize: 12, fontWeight: 800,
                      background: d <= day ? 'var(--brand)' : 'var(--surface-2)',
                      color: d <= day ? 'var(--brand-ink)' : 'var(--text-3)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {d}
                  </div>
                ))}
                <span style={{ alignSelf: 'center', marginLeft: 6, fontSize: 12, color: 'var(--text-3)', fontWeight: 700 }}>
                  Wk {week}, day {day} {day === 5 ? '· payday next!' : ''}
                </span>
              </div>
            </div>
            <Button tone="primary" onClick={advance}>{day === 5 ? 'Settle the week →' : 'Trade a day →'}</Button>
          </div>
        </Card>

        <BalanceCard />
        <ShopCard />
        <BankCard />

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <Button tone="soft" size="lg" onClick={onReflect}>Wrap up & reflect ✍️</Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Pricing Studio (supply & demand) --------------------------------

function PricingStudio({ instanceId, defId, price, marketing }: { instanceId: string; defId: string; price: number; marketing: number }) {
  const dispatch = useDispatch();
  const reputation = useGameState((s) => s.profile.reputation);
  const def = findBusiness(builderContent, defId)!;

  const pMin = Math.max(1, Math.round(def.sweetPrice * 0.4));
  const pMax = Math.round(def.sweetPrice * 1.8);

  const result = (p: number, m = marketing) =>
    businessWeeklyResult({
      baseDemand: def.baseDemand,
      price: p,
      sweetPrice: def.sweetPrice,
      marketingSpend: m,
      qualityFactor: def.qualityFactorDefault,
      repFactor: repFactor(reputation),
      capacity: def.capacity,
      cogsPerUnit: def.cogsPerUnit,
      fixedPerWeek: def.fixedPerWeek,
    });

  // Sample the curve across the price range.
  const curve = useMemo(() => {
    const pts: { p: number; units: number; profit: number }[] = [];
    for (let p = pMin; p <= pMax; p++) {
      const r = result(p);
      pts.push({ p, units: r.unitsSold, profit: r.activeIncome });
    }
    return pts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defId, marketing, reputation, pMin, pMax]);

  const sweet = useMemo(() => curve.reduce((best, c) => (c.profit > best.profit ? c : best), curve[0]!), [curve]);
  const here = result(price);

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Eyebrow color="var(--brand)">Pricing studio · supply & demand</Eyebrow>
        <Chip tone="neutral">{def.name}</Chip>
      </div>

      <DemandChart curve={curve} pMin={pMin} pMax={pMax} capacity={def.capacity} current={price} sweet={sweet.p} />

      {/* price slider */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 700 }}>Your price</span>
          <span className="bk-tnum" style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>{money(price)}</span>
        </div>
        <input
          type="range"
          min={pMin}
          max={pMax}
          step={1}
          value={price}
          onChange={(e) => dispatch({ kind: 'SetPrice', businessId: instanceId, price: Number(e.target.value) })}
          style={{ width: '100%', accentColor: 'var(--brand)' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)' }}>
          <span>cheap · lots of buyers</span>
          <span>pricey · few buyers</span>
        </div>
      </div>

      {/* live readout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
        <Tile label="Customers" value={`${here.unitsSold}`} hint={`of ${def.capacity} max`} />
        <Tile label="Revenue / wk" value={money(here.revenue)} />
        <Tile label="Profit / wk" value={<Money value={here.activeIncome} signed size={18} />} />
      </div>

      <div
        style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 12,
          background: price === sweet.p ? 'var(--up-soft)' : 'var(--brand-soft)',
          fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5,
        }}
      >
        {price === sweet.p ? (
          <span><b style={{ color: 'var(--up)' }}>Sweet spot! </b>At {money(price)} you make the most profit ({money(sweet.profit)}/wk).</span>
        ) : (
          <span>💡 The <b>sweet spot</b> is around <b>{money(sweet.p)}</b> (~{money(sweet.profit)}/wk profit). Higher price = fewer customers; lower = thin margins. Find the balance.</span>
        )}
      </div>

      {/* marketing */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)', fontWeight: 700 }}>Marketing / wk</span>
          <span className="bk-tnum" style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{money(marketing)}</span>
        </div>
        <input
          type="range"
          min={0}
          max={60}
          step={1}
          value={marketing}
          onChange={(e) => dispatch({ kind: 'SpendMarketing', businessId: instanceId, amount: Number(e.target.value) })}
          style={{ width: '100%', accentColor: 'var(--brand)' }}
        />
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          Ads bring more customers (shifts demand up) — but the spend is an expense. Worth it only if the extra sales beat the cost.
        </div>
      </div>
    </Card>
  );
}

function Tile({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div style={{ background: 'var(--surface-2)', borderRadius: 12, padding: '10px 12px', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 700 }}>{label}</div>
      <div className="bk-tnum" style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)' }}>{value}</div>
      {hint && <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{hint}</div>}
    </div>
  );
}

/** The demand curve: customers (y) vs price (x), with current + sweet-spot markers. */
function DemandChart({ curve, pMin, pMax, capacity, current, sweet }: { curve: { p: number; units: number; profit: number }[]; pMin: number; pMax: number; capacity: number; current: number; sweet: number }) {
  const W = 100, H = 56;
  const x = (p: number) => ((p - pMin) / Math.max(1, pMax - pMin)) * W;
  const y = (u: number) => H - (u / Math.max(1, capacity)) * H;
  const path = curve.map((c, i) => `${i === 0 ? 'M' : 'L'} ${x(c.p).toFixed(1)} ${y(c.units).toFixed(1)}`).join(' ');
  const area = `${path} L ${x(pMax).toFixed(1)} ${H} L ${x(pMin).toFixed(1)} ${H} Z`;
  const cur = curve.find((c) => c.p === current) ?? curve[0]!;

  return (
    <div style={{ marginTop: 12, background: 'var(--surface-2)', borderRadius: 14, padding: 12, border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>
        <span>Customers</span>
        <span>Demand curve</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="120" preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="dem" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--brand)" stopOpacity="0.28" />
            <stop offset="1" stopColor="var(--brand)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#dem)" />
        <path d={path} fill="none" stroke="var(--brand)" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
        {/* sweet spot */}
        <line x1={x(sweet)} y1="0" x2={x(sweet)} y2={H} stroke="var(--up)" strokeWidth="1" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
        {/* current */}
        <line x1={x(current)} y1="0" x2={x(current)} y2={H} stroke="var(--text-3)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        <circle cx={x(current)} cy={y(cur.units)} r="2.6" fill="var(--brand)" vectorEffect="non-scaling-stroke" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
        <span>{money(pMin)}</span>
        <span style={{ color: 'var(--up)', fontWeight: 700 }}>★ sweet {money(sweet)}</span>
        <span>{money(pMax)} (price →)</span>
      </div>
    </div>
  );
}

// ---------- Work <-> fun balance --------------------------------------------

function BalanceCard() {
  const dispatch = useDispatch();
  const happiness = useGameState(selectHappiness);
  const sprinting = useGameState((s) => s.weeklyEffects.some((e) => e.id === 'grind-edge'));
  const [err, setErr] = useState<string | null>(null);
  const zoneColor = happiness >= 50 ? 'var(--up)' : happiness >= 30 ? 'var(--warn)' : 'var(--down)';

  return (
    <Card style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Eyebrow color={zoneColor}>Work ↔ fun · your energy</Eyebrow>
        {sprinting && <Chip tone="up">⚡ Sprinting this week</Chip>}
      </div>
      <div style={{ marginTop: 8 }}>
        <Bar frac={happiness / 100} color={zoneColor} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <Button
          tone="primary"
          onClick={() => { setErr(null); dispatch({ kind: 'PushHard' }); }}
        >
          💪 Push hard
        </Button>
        <Button
          tone="soft"
          onClick={() => { setErr(null); const e = dispatch({ kind: 'TakeBreak' }); if (e) setErr(e.message); }}
        >
          ☕ Take a break
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
        <span>+12% sales this week · −energy</span>
        <span>+energy · −B$15</span>
      </div>
      {err && <p style={{ color: 'var(--down)', fontSize: 12, marginTop: 8 }}>{err}</p>}
      <p style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 10, lineHeight: 1.5 }}>
        Sprinting earns more now but drains you. Rest costs a little but keeps you sharp — burnout
        makes <i>every</i> sale harder. The best founders balance the two.
      </p>
    </Card>
  );
}

// ---------- Shop (assets) ----------------------------------------------------

function ShopCard() {
  const dispatch = useDispatch();
  const cash = useGameState((s) => s.wallet.cash);
  const assets = useGameState((s) => s.assets);
  const [err, setErr] = useState<string | null>(null);
  const vm = findAsset(builderContent, 'vendingMachine')!;
  const owns = assets.some((a) => a.defId === 'vendingMachine');

  return (
    <Card style={{ marginTop: 14 }}>
      <Eyebrow color="var(--up)">Assets · buy income</Eyebrow>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 24 }}>📚</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, color: 'var(--text)' }}>{vm.name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{money(vm.cost)} → earns {money(vm.weeklyYield)}/wk forever (passive)</div>
        </div>
        {owns ? (
          <Chip tone="up">Owned ✓</Chip>
        ) : (
          <Button
            tone="up"
            size="sm"
            disabled={cash < vm.cost}
            onClick={() => {
              setErr(null);
              const e = dispatch({ kind: 'BuyAsset', defId: 'vendingMachine' });
              if (e) setErr(e.message);
            }}
          >
            Buy
          </Button>
        )}
      </div>
      {err && <p style={{ color: 'var(--down)', fontSize: 12, marginTop: 8 }}>{err}</p>}
    </Card>
  );
}

// ---------- Bank (loans + savings) ------------------------------------------

function BankCard() {
  const dispatch = useDispatch();
  const cash = useGameState((s) => s.wallet.cash);
  const savings = useGameState((s) => s.savings);
  const loans = useGameState((s) => s.loans);
  const [deposit, setDeposit] = useState(50);
  const [err, setErr] = useState<string | null>(null);
  const loanWeekly = loans.reduce((s, l) => s + l.weeklyPayment, 0);
  const previewPayment = weeklyLoanPayment(200, builderContent.tuning.loan.ratePerWeek, builderContent.tuning.loan.defaultTermWeeks);

  return (
    <Card style={{ marginTop: 14 }}>
      <Eyebrow color="var(--brand)">Bank · savings & loans</Eyebrow>
      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Tile label="Savings jar" value={money(savings.balance)} hint={`+${(savings.ratePerWeek * 100).toFixed(0)}% / wk, compounding`} />
        <Tile label="Loan payments" value={loans.length ? `${money(-loanWeekly)}/wk` : 'none'} />
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="number"
          min={1}
          value={deposit}
          onChange={(e) => setDeposit(Number(e.target.value) || 0)}
          style={{ width: 84, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontWeight: 700 }}
        />
        <Button tone="soft" size="sm" disabled={cash < deposit || deposit <= 0} onClick={() => { setErr(null); const e = dispatch({ kind: 'Deposit', amount: deposit }); if (e) setErr(e.message); }}>Save</Button>
        <Button tone="ghost" size="sm" disabled={savings.balance < deposit || deposit <= 0} onClick={() => { setErr(null); const e = dispatch({ kind: 'Withdraw', amount: deposit }); if (e) setErr(e.message); }}>Take out</Button>
        <span style={{ flex: 1 }} />
        <Button tone="ghost" size="sm" onClick={() => { setErr(null); const e = dispatch({ kind: 'TakeLoan', principal: 200 }); if (e) setErr(e.message); }}>
          Borrow B$200 ({money(previewPayment)}/wk)
        </Button>
      </div>
      {err && <p style={{ color: 'var(--down)', fontSize: 12, marginTop: 8 }}>{err}</p>}
      <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.4 }}>
        Saving grows slowly but safely (compound interest). Borrowing gives cash now but costs more back over time.
      </p>
    </Card>
  );
}
